<template>
  <div id="inspector">
    <vue-loading :active.sync="loading" :is-full-page="true" loader="dots" color="#70A1F0"></vue-loading>

    <nav class="navbar fixed-top-sub navbar-light bg-light" v-if="active != null">
      <div class="navbar-brand">Inspector</div>
      <ul class="nav nav-pages mr-auto">
        <li class="nav-item">
          <a class="nav-link active" type="button">Findings</a>
        </li>
      </ul>

      <div class="nav-item btn-group dropdown">
        <button type="button" class="btn-block btn dropdown-toggle btn-secondary text-right" style="margin-right: 10px;" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
          {{active.account_name || active.account}}
        </button>
        <div class="dropdown-menu dropdown-menu-right scrollable-menu">
          <h6 class="dropdown-header">Accounts</h6>
          <div v-for="report in inspector" :key="report.account">
            <a class="dropdown-item" @click="setActive(report)">{{ report.account_name || report.account }}</a>
          </div>
        </div>
      </div>
      <button type="button" class="btn btn-primary text-right" @click="downloadCSV">Download Report</button>
    </nav>

    <div class="row" v-if="active != null">
      <div class="col-3">
        <div class="row">
          <transition name="fade" appear>
            <div class="card col">
              <div class="card-heading">
                <h4>Account</h4>
              </div>
              <div class="card-body">
                <dl>
                  <dt>Contract</dt><dd>{{active.contract}}</dd>
                  <dt>Account Name</dt><dd>{{active.account_name}}</dd>
                  <dt>Account Id</dt><dd>{{active.account}}</dd>
                  <dt>Total Findings</dt><dd>{{activeFindings.length}}</dd>
                  <dt>Last Updated</dt><dd>{{formatTimestamp(active.timestamp)}}</dd>
                </dl>
              </div>
            </div>
          </transition>
        </div>

        <div class="row">
          <transition name="fade" appear>
            <div class="card col">
              <div class="card-heading">
                <h4>Severity Breakdown</h4>
              </div>
              <div class="card-body">
                <div class="severity-bar" v-for="sev in severityOrder" :key="sev">
                  <span class="severity-label" :class="'severity-' + sev.toLowerCase()">{{sev}}</span>
                  <span class="severity-count">{{severityCounts[sev] || 0}}</span>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <div class="row" v-if="activeFinding != null">
          <transition name="fade" appear>
            <div class="card col">
              <div class="card-heading">
                <h4>Finding Detail</h4>
              </div>
              <div class="card-body finding-detail">
                <dl>
                  <dt>Severity</dt>
                  <dd><span :class="'severity-badge severity-' + activeFinding.severity.toLowerCase()">{{activeFinding.severity}}</span></dd>
                  <dt>Type</dt>
                  <dd>{{activeFinding.type}}</dd>
                  <dt>Score</dt>
                  <dd>{{activeFinding.inspectorScore}}</dd>
                  <dt v-if="activeFinding.packageVulnerability && activeFinding.packageVulnerability.vulnerabilityId">CVE</dt>
                  <dd v-if="activeFinding.packageVulnerability && activeFinding.packageVulnerability.vulnerabilityId">
                    <a v-if="activeFinding.packageVulnerability.sourceUrl" :href="activeFinding.packageVulnerability.sourceUrl" target="_blank">{{activeFinding.packageVulnerability.vulnerabilityId}}</a>
                    <span v-else>{{activeFinding.packageVulnerability.vulnerabilityId}}</span>
                  </dd>
                  <dt>Fix Available</dt>
                  <dd>{{activeFinding.fixAvailable || 'N/A'}}</dd>
                  <dt>Exploit Available</dt>
                  <dd>{{activeFinding.exploitAvailable || 'N/A'}}</dd>
                  <dt v-if="activeFinding.epssScore != null">EPSS</dt>
                  <dd v-if="activeFinding.epssScore != null">{{activeFinding.epssScore}}</dd>
                  <dt>Resource</dt>
                  <dd>{{activeFinding.resourceName || activeFinding.resourceId}}</dd>
                  <dt>Region</dt>
                  <dd>{{activeFinding.resourceRegion}}</dd>
                  <dt>First Observed</dt>
                  <dd>{{activeFinding.firstObservedAt}}</dd>
                  <dt>Last Observed</dt>
                  <dd>{{activeFinding.lastObservedAt}}</dd>
                </dl>

                <div v-if="activeFinding.cvssDetails" class="detail-section">
                  <h5>CVSS Score</h5>
                  <dl>
                    <dt>Score</dt>
                    <dd>{{activeFinding.cvssDetails.score}} ({{activeFinding.cvssDetails.source}})</dd>
                    <dt>Version</dt>
                    <dd>{{activeFinding.cvssDetails.version}}</dd>
                    <dt v-if="activeFinding.cvssDetails.scoringVector">Vector</dt>
                    <dd v-if="activeFinding.cvssDetails.scoringVector" class="vector-text">{{activeFinding.cvssDetails.scoringVector}}</dd>
                  </dl>
                </div>

                <div v-if="activeFinding.description" class="detail-section">
                  <h5>Description</h5>
                  <p class="description-text">{{activeFinding.description}}</p>
                </div>

                <div v-if="remediationText" class="detail-section">
                  <h5>Remediation</h5>
                  <p class="description-text">{{remediationText}}</p>
                  <a v-if="remediationUrl" :href="remediationUrl" target="_blank" class="btn btn-sm btn-outline-primary">More Info</a>
                </div>

                <div v-if="activeFinding.packageVulnerability && activeFinding.packageVulnerability.vulnerablePackages && activeFinding.packageVulnerability.vulnerablePackages.length" class="detail-section">
                  <h5>Package Versions</h5>
                  <div v-for="(pkg, idx) in activeFinding.packageVulnerability.vulnerablePackages" :key="idx" class="package-item">
                    <div class="package-name">{{pkg.name}}</div>
                    <div class="package-versions">
                      <span class="version-installed">{{pkg.version}}</span>
                      <span v-if="pkg.fixedInVersion" class="version-arrow">&rarr;</span>
                      <span v-if="pkg.fixedInVersion" class="version-fixed">{{pkg.fixedInVersion}}</span>
                    </div>
                    <div v-if="pkg.packageManager" class="package-manager">{{pkg.packageManager}}</div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <div class="col-9">
        <transition name="fade" appear>
          <vue-good-table
            :columns="findingsColumns"
            :rows="activeFindings"
            @on-row-click="onRowClick"
            :search-options="{
              enabled: true,
              skipDiacritics: true,
              placeholder: 'filter findings',
            }"
            :pagination-options="{
              enabled: true,
              perPage: 25,
            }"
            :sort-options="{
              enabled: true,
              initialSortBy: {field: 'inspectorScore', type: 'desc'}
            }"
            :row-style-class="rowStyleClassFn"
            styleClass="vgt-table condensed">
            <div slot="table-actions">
              <button type="button" class="btn btn-danger btn-margin btn-sm" @click="filterBySeverity('CRITICAL')">Critical</button>
              <button type="button" class="btn btn-warning btn-margin btn-sm" @click="filterBySeverity('HIGH')">High</button>
              <button type="button" class="btn btn-info btn-margin btn-sm" @click="filterBySeverity('MEDIUM')">Medium</button>
              <button type="button" class="btn btn-success btn-margin btn-sm" @click="showAllFindings">Show All</button>
            </div>
          </vue-good-table>
        </transition>
      </div>
    </div>

    <div class="row empty-state" v-if="!loading && inspector.length === 0">
      <div class="col text-center">
        <h4>No Inspector findings available</h4>
        <p>Inspector data will appear here once the feature is deployed and findings are reported.</p>
      </div>
    </div>
  </div>
</template>

<script>
import HTTP from '@/common/http'
import VueLoading from 'vue-loading-overlay'
import { mapGetters } from 'vuex'

export default {
  name: 'Inspector',
  props: [
    'id'
  ],
  components: {
    VueLoading
  },
  computed: {
    ...mapGetters([
      'activeType'
    ]),
    activeFindings () {
      if (!this.active || !this.active.findings) return []
      let findings = this.active.findings.map(f => ({
        ...f,
        cve: f.packageVulnerability ? f.packageVulnerability.vulnerabilityId : '',
        resourceDisplay: f.resourceName || f.resourceId
      }))
      if (this.severityFilter) {
        findings = findings.filter(f => f.severity === this.severityFilter)
      }
      return findings
    },
    remediationText () {
      if (!this.activeFinding) return ''
      const pv = this.activeFinding.packageVulnerability
      if (this.activeFinding.fixAvailable === 'YES' && pv && pv.vulnerablePackages && pv.vulnerablePackages.length) {
        const pkg = pv.vulnerablePackages[0]
        if (pkg.fixedInVersion) {
          return `Update ${pkg.name} from ${pkg.version} to ${pkg.fixedInVersion}.`
        }
      }
      return ''
    },
    remediationUrl () {
      if (!this.activeFinding) return ''
      const pv = this.activeFinding.packageVulnerability
      if (pv && pv.sourceUrl) return pv.sourceUrl
      return ''
    },
    severityCounts () {
      if (!this.active || !this.active.findings) return {}
      const counts = {}
      for (const f of this.active.findings) {
        counts[f.severity] = (counts[f.severity] || 0) + 1
      }
      return counts
    }
  },
  data () {
    return {
      loading: false,
      inspector: [],
      active: null,
      activeFinding: null,
      selectedRow: -1,
      severityFilter: null,
      severityOrder: ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'],
      findingsColumns: [
        {
          label: 'Severity',
          field: 'severity',
          width: '100px',
          filterOptions: {
            enabled: true,
            placeholder: 'All'
          }
        },
        {
          label: 'CVE',
          field: 'cve',
          width: '150px',
          formatFn: (val) => val || ''
        },
        {
          label: 'Title',
          field: 'title'
        },
        {
          label: 'Resource',
          field: 'resourceDisplay'
        },
        {
          label: 'Type',
          field: 'type',
          width: '150px'
        },
        {
          label: 'Score',
          field: 'inspectorScore',
          type: 'number',
          width: '70px'
        },
        {
          label: 'Fix',
          field: 'fixAvailable',
          width: '55px'
        },
        {
          label: 'First Observed',
          field: 'firstObservedAt',
          width: '120px'
        }
      ]
    }
  },
  methods: {
    getData () {
      this.loading = true
      this.inspector = []
      this.active = null
      this.activeFinding = null
      HTTP.get(`/inspector/${this.activeType}/${this.id}`)
        .then((response) => {
          this.inspector = response.data.inspector
          if (this.inspector.length > 0) {
            this.active = this.inspector[0]
          }
        })
        .catch((error) => {
          this.flashMessage.error({
            message: `Failed to get Inspector report: ${error.message}`
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    setActive (report) {
      this.active = report
      this.activeFinding = null
      this.selectedRow = -1
      this.severityFilter = null
    },
    onRowClick (params) {
      this.activeFinding = params.row
      this.selectedRow = params.row.originalIndex
    },
    filterBySeverity (severity) {
      this.severityFilter = severity
    },
    showAllFindings () {
      this.severityFilter = null
    },
    downloadCSV () {
      this.flashMessage.info({
        message: 'Generating Inspector CSV report',
        time: 3000
      })
      HTTP.get(`/inspector/${this.activeType}/${this.id}/csv`)
        .then((response) => {
          window.location = response.data.signedUrl
          this.flashMessage.success({
            message: 'Inspector CSV report generation complete'
          })
        })
        .catch((error) => {
          this.flashMessage.error({
            message: `Failed to generate Inspector CSV report: ${error.message}`
          })
        })
    },
    rowStyleClassFn (row) {
      if (row.originalIndex === this.selectedRow) {
        return 'selected-row'
      }
      if (row.severity === 'CRITICAL') return 'highlight-row-critical'
      if (row.severity === 'HIGH') return 'highlight-row-high'
      if (row.severity === 'MEDIUM') return 'highlight-row-medium'
      return ''
    },
    formatTimestamp (ts) {
      if (!ts) return ''
      const d = new Date(ts * 1000)
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString()
    }
  },
  watch: {
    id () {
      this.getData()
    }
  },
  created () {
    this.getData()
  }
}
</script>

<style scoped>
.row {
  display: flex;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 10px;
}

.empty-state {
  padding-top: 100px;
}

ul.nav a {
  color: rgba(0, 0, 0, 0.5);
}

ul.nav a:hover {
  cursor: pointer;
  color: rgba(0, 0, 0, 1);
}

.active {
  color: rgba(0, 0, 0, 0.8) !important;
}

.card {
  transition: opacity 200ms;
  background: #ffffff;
  width: auto;
  padding: 15px;
  border: none;
  border-radius: 0;
  box-sizing: content-box;
  box-shadow: 0 2px 13px -5px rgba(0,0,0,0.2);
  margin-bottom: 10px;
}

.btn-margin {
  margin-right: 5px;
}

.fixed-top-sub {
  position: sticky;
  top: 56px;
  z-index: 1000;
}

.scrollable-menu {
  height: auto;
  max-height: 80vh;
  overflow-x: scroll;
}

h4 {
  padding-bottom: 5px;
  font-size: 1.1rem;
}

h5 {
  font-size: 0.95rem;
  font-weight: 600;
  margin-top: 8px;
}

dt {
  font-weight: 600;
  margin-bottom: 2px;
  display: inline;
  margin-right: 5px;
}

dd {
  margin-bottom: 4px;
}

.severity-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
}

.severity-label {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 3px;
}

.severity-count {
  font-weight: 700;
  font-size: 0.9rem;
}

.severity-critical {
  color: #fff;
  background-color: #d32f2f;
}

.severity-high {
  color: #fff;
  background-color: #e65100;
}

.severity-medium {
  color: #fff;
  background-color: #f9a825;
}

.severity-low {
  color: #333;
  background-color: #e0e0e0;
}

.severity-informational {
  color: #555;
  background-color: #f5f5f5;
}

.severity-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.8rem;
  font-weight: 600;
}

.finding-detail dl {
  display: grid;
  grid-template-columns: max-content auto;
  margin: 0;
  gap: 2px 10px;
}

.finding-detail dt {
  grid-column-start: 1;
  display: block;
}

.finding-detail dd {
  grid-column-start: 2;
  display: block;
}

.description-text {
  font-size: 0.85rem;
  color: #555;
  line-height: 1.4;
}

.detail-section {
  margin-top: 10px;
  padding-top: 6px;
  border-top: 1px solid #eee;
}

.vector-text {
  font-family: monospace;
  font-size: 0.8rem;
  word-break: break-all;
}

.package-item {
  font-size: 0.85rem;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.package-name {
  font-weight: 600;
  margin-bottom: 2px;
}

.package-versions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.version-installed {
  color: #d32f2f;
  font-family: monospace;
  font-size: 0.82rem;
}

.version-arrow {
  color: #888;
}

.version-fixed {
  color: #2e7d32;
  font-weight: 600;
  font-family: monospace;
  font-size: 0.82rem;
}

.package-manager {
  font-size: 0.75rem;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.highlight-row-critical {
  background-color: rgba(211, 47, 47, 0.08) !important;
}

.highlight-row-high {
  background-color: rgba(230, 81, 0, 0.06) !important;
}

.highlight-row-medium {
  background-color: rgba(249, 168, 37, 0.06) !important;
}

.selected-row {
  background-color: rgba(112, 161, 240, 0.15) !important;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 200ms ease-in-out;
}
.fade-enter-to {
  opacity: 1;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/Inspector.vue